/**
 * Text comparison for a beginner typing Portuguese on a phone keyboard.
 *
 * The pedagogical stance, decided once here so every exercise type behaves the
 * same way: accents are *meaningful* in Portuguese (`avô` grandfather vs `avó`
 * grandmother), so a missing accent is wrong by default. But an exercise can
 * opt into `accentTolerant`, in which case an accent-only miss is accepted and
 * reported back as a nudge rather than a failure. Punctuation and case are
 * never graded — they teach nothing about the language.
 */

/** Curly quotes, non-breaking spaces, and other things phone keyboards emit. */
const CHAR_FIXUPS: Array<[RegExp, string]> = [
  [/[‘’‛ʼ]/g, "'"],
  [/[“”‟]/g, '"'],
  [/[–—]/g, '-'],
  [/[   ]/g, ' '],
  [/…/g, '...'],
]

/** Punctuation stripped before comparison. Keeps intra-word hyphen/apostrophe. */
const TRIM_PUNCT = /^[\s"'¿¡(){}[\].,!?;:…-]+|[\s"'?!.,;:…)(-]+$/g

/**
 * Canonical form for comparison: NFC, fixups, lowercased, punctuation-trimmed,
 * whitespace collapsed. Accents are preserved.
 */
export function normalizeText(input: string): string {
  let s = input.normalize('NFC')
  for (const [pattern, replacement] of CHAR_FIXUPS) s = s.replace(pattern, replacement)
  s = s.toLowerCase().replace(/\s+/g, ' ').trim()
  // Strip edge punctuation from the string and from each word.
  s = s.replace(TRIM_PUNCT, '')
  s = s
    .split(' ')
    .map((w) => w.replace(TRIM_PUNCT, ''))
    .filter(Boolean)
    .join(' ')
  return s
}

/** Removes diacritics: 'não' → 'nao'. Only for accent-tolerant comparison. */
export function foldAccents(input: string): string {
  return input.normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC')
}

export function tokenize(input: string): string[] {
  const n = normalizeText(input)
  return n ? n.split(' ') : []
}

export type MatchKind = 'exact' | 'accent' | 'none'

export interface MatchResult {
  kind: MatchKind
  /** The accepted answer that matched, in its original form. */
  matched?: string
  /** True when the only problem was a missing or wrong accent. */
  accentSlip: boolean
}

/**
 * Compares a typed answer against the accepted alternatives.
 *
 * Returns `accent` when the answer is right apart from diacritics. The caller
 * decides whether that counts, based on the exercise's `accentTolerant` flag —
 * this function never makes that policy decision itself.
 */
export function matchAnswer(input: string, accepted: readonly string[]): MatchResult {
  const normalizedInput = normalizeText(input)
  if (!normalizedInput) return { kind: 'none', accentSlip: false }

  for (const candidate of accepted) {
    if (normalizeText(candidate) === normalizedInput) {
      return { kind: 'exact', matched: candidate, accentSlip: false }
    }
  }

  const foldedInput = foldAccents(normalizedInput)
  for (const candidate of accepted) {
    if (foldAccents(normalizeText(candidate)) === foldedInput) {
      return { kind: 'accent', matched: candidate, accentSlip: true }
    }
  }

  return { kind: 'none', accentSlip: false }
}

export interface WordDiffEntry {
  expected?: string
  actual?: string
  status: 'correct' | 'accent' | 'wrong' | 'missing' | 'extra'
}

/**
 * Word-by-word comparison used for translation feedback and, in Phase 3, for
 * speaking attempts. Deliberately positional rather than a full edit-distance
 * alignment: for short beginner sentences it produces feedback that is easier
 * to read, and it never claims more precision than it has.
 */
export function diffWords(expected: string, actual: string): WordDiffEntry[] {
  const exp = tokenize(expected)
  const act = tokenize(actual)
  const out: WordDiffEntry[] = []

  for (let i = 0; i < Math.max(exp.length, act.length); i++) {
    const e = exp[i]
    const a = act[i]
    if (e === undefined) out.push({ actual: a, status: 'extra' })
    else if (a === undefined) out.push({ expected: e, status: 'missing' })
    else if (e === a) out.push({ expected: e, actual: a, status: 'correct' })
    else if (foldAccents(e) === foldAccents(a))
      out.push({ expected: e, actual: a, status: 'accent' })
    else out.push({ expected: e, actual: a, status: 'wrong' })
  }

  return out
}

/** Fraction of expected words present anywhere in the answer, 0..1. */
export function wordCoverage(expected: string, actual: string): number {
  const exp = tokenize(expected)
  if (exp.length === 0) return 1
  const act = new Set(tokenize(actual).map(foldAccents))
  const hits = exp.filter((w) => act.has(foldAccents(w))).length
  return hits / exp.length
}
